import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { 
  DndContext, 
  DragOverlay, 
  useSensor, 
  useSensors, 
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCorners,
  useDraggable
} from '@dnd-kit/core';
import { 
  restrictToWindowEdges,
  restrictToParentElement 
} from '@dnd-kit/modifiers';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DocumentTextIcon, 
  PlayIcon, 
  PhotoIcon, 
  SpeakerWaveIcon, 
  LinkIcon,
  CursorArrowRaysIcon,
  SparklesIcon,
  TrashIcon,
  ArrowsPointingOutIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';

// Optimized canvas dimensions for better performance
const CANVAS_WIDTH = 2400;
const CANVAS_HEIGHT = 1600;
const GRID_SIZE = 20;

// Enhanced Draggable Block Component with resize handles
const DraggableBlock = React.memo(({ 
  block, 
  isSelected, 
  isConnected, 
  onSelect, 
  onDelete, 
  onConnect,
  onUpdate,
  isReadOnly 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(block.content_text || '');
  const [isResizing, setIsResizing] = useState(false);
  const textareaRef = useRef(null);
  
  const config = BLOCK_CONFIGS[block.block_type] || BLOCK_CONFIGS.note;
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: block.id,
    disabled: isReadOnly || isResizing
  });

  // Debug logging
  useEffect(() => {
    console.log(`Block ${block.id}: isDragging=${isDragging}, transform=`, transform);
  }, [isDragging, transform, block.id]);
  
  const style = {
    left: block.position_x,
    top: block.position_y,
    width: block.width,
    height: block.height,
    zIndex: isSelected ? 1000 : block.z_index || 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  // Handle edit mode
  const handleStartEdit = useCallback((e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditContent(block.content_text || '');
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [block.content_text]);

  const handleSaveEdit = useCallback(() => {
    if (editContent !== block.content_text) {
      onUpdate(block.id, { content_text: editContent });
      toast.success('Block updated!');
    }
    setIsEditing(false);
  }, [block.id, block.content_text, editContent, onUpdate]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditContent(block.content_text || '');
  }, [block.content_text]);

  // Handle resize
  const handleResize = useCallback((e, direction) => {
    e.stopPropagation();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = block.width;
    const startHeight = block.height;
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      
      if (direction.includes('e')) newWidth = Math.max(200, startWidth + deltaX);
      if (direction.includes('s')) newHeight = Math.max(120, startHeight + deltaY);
      
      onUpdate(block.id, { width: newWidth, height: newHeight });
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      toast.success('Block resized!');
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [block.id, block.width, block.height, onUpdate]);
  
  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      whileHover={{ scale: isDragging ? 1 : 1.02 }}
      whileTap={{ scale: isDragging ? 1 : 0.98 }}
      className={`
        absolute bg-white rounded-xl shadow-lg border-2 transition-all duration-200
        ${isDragging ? 'cursor-grabbing shadow-2xl scale-105' : isResizing ? 'cursor-default' : 'cursor-grab'}
        ${isSelected ? 'border-purple-500 shadow-purple-200' : 
          isConnected ? 'border-purple-300' : 'border-gray-200'}
        ${!isReadOnly ? 'hover:shadow-xl' : ''}
        ${isDragging ? 'opacity-80' : 'opacity-100'}
        ${isEditing ? 'ring-2 ring-blue-300' : ''}
      `}
      style={style}
      onClick={() => onSelect(block.id)}
      onDoubleClick={handleStartEdit}
    >
      {/* Block Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isConnected ? 'bg-purple-100' : 'bg-gray-100'
          }`}>
            <config.icon className={`w-4 h-4 ${
              isConnected ? 'text-purple-600' : 'text-gray-600'
            }`} />
          </div>
          <span className="font-medium text-gray-900 text-sm truncate">
            {block.title || `${block.block_type.charAt(0).toUpperCase() + block.block_type.slice(1)}`}
          </span>
        </div>
        
        {isSelected && !isReadOnly && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleStartEdit}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Edit content"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConnect(block.id);
              }}
              className="p-1 text-purple-600 hover:bg-purple-50 rounded"
              title="Connect to AI"
            >
              <SparklesIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(block.id);
              }}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Delete (Del key)"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Block Content */}
      <div className="p-3 flex-1">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveEdit();
                } else if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
              className="w-full h-20 text-sm border border-gray-300 rounded p-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter content..."
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                Save (Ctrl+Enter)
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
              >
                Cancel (Esc)
              </button>
            </div>
          </div>
        ) : (
          <>
            {block.content_text && (
              <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                {block.content_text}
              </p>
            )}
            {block.content_url && (
              <div className="mt-2 text-xs text-blue-600 truncate">
                <a href={block.content_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {block.content_url}
                </a>
              </div>
            )}
            {!block.content_text && !block.content_url && (
              <p className="text-sm text-gray-400 italic">
                Double-click to edit content
              </p>
            )}
          </>
        )}
      </div>

      {/* Connection Indicator */}
      {isConnected && (
        <div className="absolute top-2 right-2">
          <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
        </div>
      )}

      {/* Resize Handles */}
      {isSelected && !isReadOnly && !isEditing && (
        <>
          {/* Bottom-right resize handle */}
          <div
            onMouseDown={(e) => handleResize(e, 'se')}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize hover:bg-purple-100 rounded-tl-lg"
            style={{ background: 'linear-gradient(-45deg, transparent 30%, #9333ea 30%, #9333ea 70%, transparent 70%)' }}
          />
          {/* Right edge resize handle */}
          <div
            onMouseDown={(e) => handleResize(e, 'e')}
            className="absolute top-4 right-0 bottom-4 w-2 cursor-e-resize hover:bg-purple-100 opacity-0 hover:opacity-100 transition-opacity"
          />
          {/* Bottom edge resize handle */}
          <div
            onMouseDown={(e) => handleResize(e, 's')}
            className="absolute bottom-0 left-4 right-4 h-2 cursor-s-resize hover:bg-purple-100 opacity-0 hover:opacity-100 transition-opacity"
          />
        </>
      )}
    </motion.div>
  );
});

// Chat Node Component with React.memo
const ChatNode = React.memo(({ 
  chatNode, 
  isSelected, 
  connectedCount, 
  onSelect,
  isReadOnly 
}) => {
  if (!chatNode) return null;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: 'chat',
    disabled: isReadOnly
  });

  const style = {
    left: chatNode.position_x,
    top: chatNode.position_y,
    width: chatNode.width,
    height: chatNode.height,
    zIndex: isSelected ? 1001 : 999,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: isDragging ? 1 : 1.02 }}
      className={`
        absolute bg-gradient-to-br from-purple-500 to-indigo-600 
        rounded-2xl shadow-xl text-white transition-all duration-200
        ${isDragging ? 'cursor-grabbing shadow-2xl scale-105' : 'cursor-grab'}
        ${isSelected ? 'ring-4 ring-purple-300' : ''}
        ${isDragging ? 'opacity-90' : 'opacity-100'}
      `}
      style={style}
      onClick={() => onSelect('chat')}
    >
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
          <SparklesIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-white">AI Assistant</h3>
          <p className="text-sm text-white/80">
            {connectedCount > 0 
              ? `Connected to ${connectedCount} block${connectedCount > 1 ? 's' : ''}`
              : 'Ready to connect'
            }
          </p>
        </div>
      </div>

      {/* Chat Status */}
      <div className="px-4 pb-4">
        <div className="bg-white/10 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-white/90">Online & Ready</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

// Connection Lines Component
const ConnectionLines = React.memo(({ connections, canvasBlocks, chatNode }) => {
  if (!chatNode || connections.length === 0) return null;

  return (
    <svg 
      className="absolute inset-0 pointer-events-none"
      style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
    >
      {connections.map(connection => {
        const sourceBlock = canvasBlocks.find(b => b.id === connection.source_block_id);
        if (!sourceBlock) return null;

        const sourceX = sourceBlock.position_x + sourceBlock.width;
        const sourceY = sourceBlock.position_y + sourceBlock.height / 2;
        const targetX = chatNode.position_x;
        const targetY = chatNode.position_y + chatNode.height / 2;

        // Create smooth curved path
        const midX = (sourceX + targetX) / 2;
        const controlX1 = sourceX + (midX - sourceX) * 0.5;
        const controlX2 = targetX - (targetX - midX) * 0.5;

        return (
          <motion.path
            key={connection.id}
            d={`M ${sourceX} ${sourceY} C ${controlX1} ${sourceY}, ${controlX2} ${targetY}, ${targetX} ${targetY}`}
            stroke="#8b5cf6"
            strokeWidth="2"
            fill="none"
            strokeDasharray="8,4"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="drop-shadow-sm"
          />
        );
      })}
    </svg>
  );
});

// Block configurations
const BLOCK_CONFIGS = {
  note: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#8b5cf6',
    icon: DocumentTextIcon,
    defaultSize: { width: 280, height: 180 }
  },
  video: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#6366f1',
    icon: PlayIcon,
    defaultSize: { width: 320, height: 240 }
  },
  image: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#8b5cf6',
    icon: PhotoIcon,
    defaultSize: { width: 280, height: 200 }
  },
  pdf: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#6366f1',
    icon: DocumentTextIcon,
    defaultSize: { width: 260, height: 200 }
  },
  audio: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#8b5cf6',
    icon: SpeakerWaveIcon,
    defaultSize: { width: 300, height: 120 }
  },
  website: { 
    color: '#fefefe', 
    borderColor: '#e1e5e9', 
    accentColor: '#6366f1',
    icon: LinkIcon,
    defaultSize: { width: 320, height: 200 }
  }
};

// Main WhiteboardCanvas Component
const WhiteboardCanvas = ({ 
  boardId, 
  canvasBlocks = [], 
  chatNode = null, 
  connections = [],
  onBlockCreate,
  onBlockUpdate,
  onBlockDelete,
  onBlockConnect,
  onChatMessage,
  isReadOnly = false 
}) => {
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const canvasRef = useRef(null);

  // Optimized sensors for better touch and mouse support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Debug logging
  useEffect(() => {
    console.log('WhiteboardCanvas rendered with blocks:', canvasBlocks.length);
    console.log('Active ID:', activeId);
  }, [canvasBlocks.length, activeId]);

  // Debounced update function for better performance
  const debouncedUpdate = useMemo(
    () => debounce((blockId, updates) => {
      console.log('Updating block:', blockId, updates);
      onBlockUpdate(blockId, updates);
    }, 100),
    [onBlockUpdate]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isReadOnly) return;
      
      // Delete selected block with Delete or Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBlockId && selectedBlockId !== 'chat') {
        e.preventDefault();
        onBlockDelete(selectedBlockId);
        setSelectedBlockId(null);
        toast.success('Block deleted!');
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedBlockId(null);
      }
      
      // Copy block with Ctrl+C (future feature)
      if (e.key === 'c' && e.ctrlKey && selectedBlockId && selectedBlockId !== 'chat') {
        // TODO: Implement block copying
        toast('Block copying coming soon!', { icon: '📋' });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedBlockId, onBlockDelete, isReadOnly]);

  // Handle drag start
  const handleDragStart = useCallback((event) => {
    if (isReadOnly) return;
    console.log('Drag started:', event.active.id);
    setActiveId(event.active.id);
    toast('Dragging block...', { icon: '🎯', duration: 1000 });
  }, [isReadOnly]);

  // Handle drag end with optimized updates
  const handleDragEnd = useCallback((event) => {
    const { active, delta } = event;
    console.log('Drag ended:', active?.id, delta);
    
    if (!active || isReadOnly) {
      setActiveId(null);
      return;
    }

    const blockId = active.id;
    
    if (blockId === 'chat') {
      // Handle chat node movement (local state only for now)
      toast.success('Chat node moved!');
      setActiveId(null);
      return;
    }

    // Find the block being dragged
    const block = canvasBlocks.find(b => b.id === blockId);
    if (!block) {
      setActiveId(null);
      return;
    }

    // Calculate new position
    const newPosition = {
      position_x: Math.max(0, Math.min(CANVAS_WIDTH - block.width, block.position_x + delta.x)),
      position_y: Math.max(0, Math.min(CANVAS_HEIGHT - block.height, block.position_y + delta.y))
    };

    // Only update if position actually changed
    if (newPosition.position_x !== block.position_x || newPosition.position_y !== block.position_y) {
      debouncedUpdate(blockId, newPosition);
      toast.success('Block moved!');
    }

    setActiveId(null);
  }, [canvasBlocks, debouncedUpdate, isReadOnly]);

  // Handle block selection
  const handleBlockSelect = useCallback((blockId) => {
    console.log('Block selected:', blockId);
    setSelectedBlockId(prevSelected => prevSelected === blockId ? null : blockId);
  }, []);

  // Handle block deletion
  const handleBlockDelete = useCallback((blockId) => {
    if (isReadOnly) return;
    console.log('Deleting block:', blockId);
    onBlockDelete(blockId);
    setSelectedBlockId(null);
    toast.success('Block deleted!');
  }, [onBlockDelete, isReadOnly]);

  // Handle block connection
  const handleBlockConnect = useCallback((blockId) => {
    if (isReadOnly || !chatNode) return;
    console.log('Connecting block to AI:', blockId);
    onBlockConnect(blockId, chatNode.id);
    toast.success('Block connected to AI!');
  }, [onBlockConnect, chatNode, isReadOnly]);

  // Handle canvas click (deselect)
  const handleCanvasClick = useCallback((e) => {
    if (e.target === canvasRef.current || e.target.closest('.canvas-background')) {
      setSelectedBlockId(null);
    }
  }, []);

  // Generate grid background
  const gridBackground = useMemo(() => (
    <div 
      className="absolute inset-0 opacity-30 canvas-background"
      style={{
        backgroundImage: `
          linear-gradient(to right, #f1f5f9 1px, transparent 1px),
          linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)
        `,
        backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`
      }}
    />
  ), []);

  // Memoized draggable items for performance
  const draggableItems = useMemo(() => {
    const items = canvasBlocks.map(block => ({
      id: block.id,
      data: block
    }));
    
    if (chatNode) {
      items.push({
        id: 'chat',
        data: chatNode
      });
    }
    
    return items;
  }, [canvasBlocks, chatNode]);

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={3}
        limitToBounds={false}
        doubleClick={{ disabled: true }}
        wheel={{ step: 0.05 }}
        pinch={{ step: 5 }}
        centerOnInit={false}
        disablePadding={true}
        panning={{
          disabled: false,
          velocityDisabled: true,
          lockAxisX: false,
          lockAxisY: false
        }}
      >
        <TransformComponent
          wrapperClass="w-full h-full"
          contentClass="w-full h-full"
          wrapperStyle={{
            width: '100%',
            height: '100%',
            touchAction: 'none'
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement]}
          >
            <div 
              ref={canvasRef}
              className="relative canvas-background"
              style={{ 
                width: CANVAS_WIDTH, 
                height: CANVAS_HEIGHT,
                touchAction: 'none'
              }}
              onClick={handleCanvasClick}
            >
              {/* Grid Background */}
              {gridBackground}

              {/* Connection Lines */}
              <ConnectionLines 
                connections={connections}
                canvasBlocks={canvasBlocks}
                chatNode={chatNode}
              />

              {/* Draggable Blocks */}
              <AnimatePresence>
                {canvasBlocks.map(block => (
                  <DraggableBlock
                    key={block.id}
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    isConnected={connections.some(conn => conn.source_block_id === block.id)}
                    onSelect={handleBlockSelect}
                    onDelete={handleBlockDelete}
                    onConnect={handleBlockConnect}
                    onUpdate={onBlockUpdate}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </AnimatePresence>

              {/* Chat Node */}
              {chatNode && (
                <ChatNode
                  chatNode={chatNode}
                  isSelected={selectedBlockId === 'chat'}
                  connectedCount={connections.length}
                  onSelect={handleBlockSelect}
                  isReadOnly={isReadOnly}
                />
              )}

              {/* Drag Overlay for smooth dragging */}
              <DragOverlay>
                {activeId ? (
                  <div className="opacity-50 pointer-events-none">
                    {activeId === 'chat' ? (
                      <div className="w-96 h-64 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-xl" />
                    ) : (
                      <div className="w-64 h-48 bg-white rounded-xl shadow-xl border-2 border-purple-300" />
                    )}
                  </div>
                ) : null}
              </DragOverlay>
            </div>
          </DndContext>
        </TransformComponent>
      </TransformWrapper>

      {/* Canvas Controls & Instructions */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <div className="bg-white rounded-lg shadow-lg p-3 max-w-xs">
          <div className="text-xs text-gray-500 space-y-1">
            <div className="font-medium text-gray-700">Canvas Controls:</div>
            <div>• Click to select • Double-click to edit</div>
            <div>• Drag to move • Drag corners to resize</div>
            <div>• Delete/Backspace to delete</div>
            <div>• Mouse wheel to zoom</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg p-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {canvasBlocks.length} blocks • {connections.length} connections
            {selectedBlockId && selectedBlockId !== 'chat' && ' • 1 selected'}
          </span>
        </div>
      </div>

      {/* Selection indicator */}
      {selectedBlockId && selectedBlockId !== 'chat' && !isReadOnly && (
        <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
          Block selected - Press Delete to remove
        </div>
      )}

      {/* Debug info */}
      <div className="absolute top-4 right-4 bg-black text-white p-2 rounded text-xs opacity-70">
        Active: {activeId || 'none'} | Blocks: {canvasBlocks.length}
      </div>
    </div>
  );
};

export default React.memo(WhiteboardCanvas);